/**
 * `RTCPeerConnection.getStats()` -> jitter, loss, RTT, codec, relay flag. Pure over an array
 * of stats records so vectors can be tested; loss is computed over the interval since the
 * previous sample when one is given, else cumulative.
 */
export interface StatsCounters {
  packetsReceived: number;
  packetsLost: number;
}

export interface StatsSummary {
  jitterMs: number;
  lossPct: number;
  rttMs: number;
  codec: string;
  relayed: boolean;
  counters: StatsCounters;
}

type Rec = Record<string, unknown>;
const num = (v: unknown, d = 0): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);

export function summariseStats(records: Iterable<Rec>, prev?: StatsCounters): StatsSummary {
  const byId = new Map<string, Rec>();
  const all: Rec[] = [];
  for (const r of records) {
    all.push(r);
    const id = str(r['id']);
    if (id) byId.set(id, r);
  }
  const inbound = all.find((r) => r['type'] === 'inbound-rtp' && (r['kind'] === 'audio' || r['mediaType'] === 'audio')) ?? all.find((r) => r['type'] === 'inbound-rtp');
  const jitterMs = inbound ? num(inbound['jitter']) * 1000 : 0;
  const received = inbound ? num(inbound['packetsReceived']) : 0;
  const lost = inbound ? num(inbound['packetsLost']) : 0;
  let dr = received;
  let dl = lost;
  if (prev) {
    dr = Math.max(0, received - prev.packetsReceived);
    dl = Math.max(0, lost - prev.packetsLost);
  }
  const lossPct = dr + dl > 0 ? (100 * dl) / (dr + dl) : 0;

  let codec = 'unknown';
  const codecId = inbound ? str(inbound['codecId']) : undefined;
  const codecRec = codecId ? byId.get(codecId) : all.find((r) => r['type'] === 'codec');
  const mime = codecRec ? str(codecRec['mimeType']) : undefined;
  if (mime) codec = mime.replace(/^audio\//i, '');

  // selected candidate pair: transport.selectedCandidatePairId, else a nominated succeeded pair
  const transport = all.find((r) => r['type'] === 'transport' && str(r['selectedCandidatePairId']));
  let pair = transport ? byId.get(str(transport['selectedCandidatePairId']) ?? '') : undefined;
  if (!pair) pair = all.find((r) => r['type'] === 'candidate-pair' && r['state'] === 'succeeded' && (r['nominated'] === true || r['selected'] === true));
  if (!pair) pair = all.find((r) => r['type'] === 'candidate-pair' && r['state'] === 'succeeded');
  const rttMs = pair ? num(pair['currentRoundTripTime']) * 1000 : 0;
  let relayed = false;
  if (pair) {
    const local = byId.get(str(pair['localCandidateId']) ?? '');
    const remote = byId.get(str(pair['remoteCandidateId']) ?? '');
    relayed = local?.['candidateType'] === 'relay' || remote?.['candidateType'] === 'relay';
  }
  return {
    jitterMs: Math.round(jitterMs * 10) / 10,
    lossPct: Math.round(lossPct * 100) / 100,
    rttMs: Math.round(rttMs),
    codec,
    relayed,
    counters: { packetsReceived: received, packetsLost: lost },
  };
}

export async function collectStats(pc: Pick<RTCPeerConnection, 'getStats'>, prev?: StatsCounters): Promise<StatsSummary> {
  const report = await pc.getStats();
  const records: Rec[] = [];
  report.forEach((v: unknown) => {
    if (v && typeof v === 'object') records.push(v as Rec);
  });
  return summariseStats(records, prev);
}
